/**
 * Manages bidirectional `postMessage` communication between the host page and a
 * specific third-party `<iframe>` element.
 *
 * ## Responsibilities
 * - **Receiving** — Listens on `window.addEventListener('message', …)` and routes
 *   incoming messages to registered handlers, after validating that the message
 *   comes from the expected origin **and** from the correct iFrame (identified by
 *   `message.source`).
 * - **Sending** — Posts typed messages to the iFrame's `contentWindow`, merging in
 *   a fixed `outgoingMessageConfig` envelope (e.g. `{ source: 'propstream', version: 1 }`)
 *   so the iFrame always knows who the sender is.
 *
 * ## Message shape
 * Every message (incoming and outgoing) is expected to carry at minimum:
 * ```js
 * {
 *   source:  string,  // iFrame HTML id (incoming) / host identifier (outgoing)
 *   version: number,  // protocol version
 *   type:    string,  // message type key used for routing
 *   // …plus any message-specific payload fields
 * }
 * ```
 * When a handler is invoked the `source`, `version`, and `type` fields are stripped
 * from the payload object so handlers only receive business data.
 *
 * ## Usage example (Ballpoint Direct Mail iFrame)
 * ```js
 * const handler = new IFrameMessageHandler(
 *   'ballpoint-direct-mail',            // id attribute of the <iframe> DOM element
 *   BALLPOINT_IFRAME_URL,               // origin to trust for incoming messages
 *   'ballpoint',                        // expected message.source value from the iFrame
 *   { source: 'propstream', version: 1 }  // merged into every outgoing message
 * );
 *
 * // Subscribe to inbound events from the iFrame
 * handler.subscribeToMessage('campaign_created', ({ campaignId, campaignType }) => {
 *   createDirectMailCampaign(campaignId, campaignType);
 * });
 *
 * // Push configuration data into the iFrame
 * handler.sendMessage('set_api_config', { apiBaseUrl, apiToken });
 * ```
 *
 * ## Important constraints
 * - **One handler per message type** — `subscribeToMessage` uses a `Map`; calling it
 *   twice with the same `messageType` silently replaces the previous handler.
 * - **Single iFrame per instance** — The handler is scoped to one iFrame DOM element.
 *   Create separate instances for separate iFrames.
 * - **No cleanup / unsubscribe** — The `window` `'message'` listener is never removed.
 *   Instantiate this class once per component lifetime (e.g. in a React `useEffect`)
 *   and do not create new instances on every render.
 * - **`iFrameHTMLId` vs `iFrameMessageSource` are independent** — The DOM element id
 *   and the `message.source` routing key are separate concerns and may differ. Use
 *   `iFrameHTMLId` to locate the element; use `iFrameMessageSource` to match the
 *   source field the iFrame stamps on its outbound messages.
 */
/**
 * Module-level dedup registry shared across all IFrameMessageHandler instances.
 * Keyed by `"${iFrameMessageSource}:${JSON.stringify(message)}"` — the entire
 * message content, not just its type — so multiple instances (e.g. from a rapid
 * unmount/remount cycle) don't each process the same exact message, while distinct
 * messages of the same type never collide with one another.
 *
 * An entry is added when a message is received and removed once its handler has
 * finished (see `initialize()`), so it only guards against an identical message
 * arriving again while the previous one is still being handled.
 *
 * @type {Set<string>}
 */
const _lastMessageRegistry = new Set();

class IFrameMessageHandler {
  /**
   * Creates a new IFrameMessageHandler and immediately begins listening for
   * `postMessage` events from the specified iFrame origin.
   *
   * @param {string} iFrameHTMLId
   *   The `id` attribute of the `<iframe>` DOM element this handler is bound to.
   *   Used **only** for the `document.getElementById` lookup that resolves the
   *   element reference for outgoing messages. Not used for message routing.
   *
   * @param {string} iFrameOriginUrl
   *   The expected origin of the iFrame (e.g. `"https://app.ballpoint.com"`).
   *   Incoming messages from any other origin are silently ignored (security guard).
   *   Also used as the `targetOrigin` argument of `postMessage` for outgoing messages.
   *
   * @param {string} iFrameMessageSource
   *   The value the iFrame stamps in `message.source` on every message it sends.
   *   Used to disambiguate messages when multiple iFrames share the same origin.
   *   Must match exactly — messages with a different or missing `source` are dropped.
   *
   * @param {Object} outgoingMessageConfig
   *   A fixed envelope object merged into **every** outgoing message before it is
   *   posted. Typically identifies the host to the iFrame, e.g.:
   *   `{ source: 'propstream', version: 1 }`.
   *
   * @param {Function} [showToast]
   *   Optional `(message, type) => void` toast trigger (e.g. from `useToast()`). When
   *   provided, any registered handler that throws or rejects — an unexpected message
   *   shape from the iFrame, a bug in the handler, anything — surfaces a generic error
   *   toast to the user instead of failing silently. See the serial queue's `.catch()`
   *   in `initialize()`, which is the single place every handler's errors funnel through.
   */
  constructor(iFrameHTMLId, iFrameOriginUrl, iFrameMessageSource, outgoingMessageConfig, showToast) {
    this.iFrameHTMLId = iFrameHTMLId;
    this.iFrameOriginUrl = iFrameOriginUrl;
    this.iFrameMessageSource = iFrameMessageSource;
    this.outgoingMessageConfig = outgoingMessageConfig;
    this.showToast = showToast;

    /** @type {HTMLIFrameElement} The resolved iFrame DOM element. */
    this.element = document.getElementById(iFrameHTMLId);

    /**
     * Maps message type strings to their registered handler functions.
     * Only one handler is allowed per type; registering a new handler for an
     * existing type replaces the previous one.
     *
     * @type {Map<string, Function>}
     */
    this.messageSubscriptionMap = new Map();

    /** @type {Promise<void>} Serial queue — each handler runs only after the previous one resolves. */
    this._queue = Promise.resolve();

    this.initialize();
  }

  /**
   * Attaches the global `window` `'message'` listener that routes inbound
   * `postMessage` events to registered handlers.
   *
   * Two security checks are applied before any handler is invoked:
   * 1. **Origin check** — `event.origin` must equal `this.iFrameOriginUrl`.
   *    Messages from any other origin are dropped.
   * 2. **Source check** — `event.data.source` must equal `this.iFrameMessageSource`.
   *    This ensures that when multiple iFrames share the same origin, only
   *    messages from *this* iFrame are processed. The source key is provided
   *    explicitly at construction time and is independent of the DOM element id.
   *
   * If both checks pass and a handler is registered for `message.type`, the
   * handler is called with the message payload minus the routing fields
   * (`source`, `version`, `type`).
   *
   * @private
   */
  initialize() {
    this._boundMessageHandler = (event) => {
      // Only accept messages from the iFrame origin
      if (event.origin !== this.iFrameOriginUrl) return;

      const message = event.data;
      // Only accept messages with the predefined iFrame message source
      if (!message || message.source !== this.iFrameMessageSource) return;

      const hasHandler = this.messageSubscriptionMap.has(message.type);
      console.log(
        `[IFrameMessageHandler] <- received "${message.type}" — handler: ${hasHandler ? 'yes' : 'NO'}`,
        message
      );

      if (this.messageSubscriptionMap.has(message.type)) {
        // Drop this message if an identical one (same source + full content) is
        // already being handled — see the `_lastMessageRegistry` doc above.
        const registryKey = `${this.iFrameMessageSource}:${JSON.stringify(message)}`;
        if (_lastMessageRegistry.has(registryKey)) return;
        _lastMessageRegistry.add(registryKey);

        const { source, version, type, ...updatedMessage } = message;
        const handler = this.messageSubscriptionMap.get(message.type);
        // Chain onto the serial queue so handlers run one at a time in arrival order,
        // even when a handler is async. The .catch() resets the chain on failure so
        // a single bad handler doesn't prevent subsequent messages from being processed.
        // This is also the single funnel point for every handler's errors — an unexpected
        // message shape from the iFrame, a bug in the handler, anything — so it's where a
        // generic fallback toast is shown when the handler itself hasn't already surfaced
        // something more specific to the user.
        this._queue = this._queue
          .then(() => handler(updatedMessage))
          .catch((err) => {
            console.error('IFrameMessageHandler: handler error', err);
            this.showToast?.("Something went wrong. Please try again.", "error");
          })
          .finally(() => {
            _lastMessageRegistry.delete(registryKey);
          });
      }
    };
    window.addEventListener('message', this._boundMessageHandler);
  }

  /**
   * Removes the global `window` `'message'` listener registered by this instance.
   * Call this when the component that owns this handler unmounts to prevent
   * stale listeners accumulating across remounts.
   */
  destroy() {
    window.removeEventListener('message', this._boundMessageHandler);
    _lastMessageRegistry.clear();
  }

  /**
   * Re-resolves the iFrame DOM element reference.
   *
   * Call this after the underlying `<iframe>` is remounted (e.g. a React `key` change).
   * The old element gets detached from the DOM, so without this `sendMessage` would
   * keep posting into the stale, detached node instead of the newly mounted iframe.
   */
  reset() {
    this.element = document.getElementById(this.iFrameHTMLId);
  }

  /**
   * Registers a handler function to be called when the iFrame sends a message
   * of the given type.
   *
   * Calling this method a second time with the same `messageType` **replaces**
   * the previously registered handler — there is no multi-listener support per type.
   *
   * @param {string}   messageType - The `type` field value to match on incoming messages.
   * @param {Function} handler     - Callback invoked with the message payload (the full
   *                                 `event.data` object minus `source`, `version`, and `type`).
   *
   * @example
   * handler.subscribeToMessage('campaign_submitted', ({ orders }) => {
   *   handleDirectMailCampaignOrdersCreation(orders);
   * });
   */
  subscribeToMessage(messageType, handler) {
    this.messageSubscriptionMap.set(messageType, handler);
  }

  /**
   * Posts a typed message to the iFrame via `contentWindow.postMessage`.
   *
   * The final posted object is:
   * ```js
   * {
   *   ...outgoingMessageConfig,  // e.g. { source: 'propstream', version: 1 }
   *   type: messageType,
   *   ...message                 // caller-supplied payload fields
   * }
   * ```
   * The `targetOrigin` is always `this.iFrameOriginUrl`, so the browser will
   * reject the post if the iFrame has navigated away from the expected origin.
   *
   * @param {string}      messageType - The message type identifier (e.g. `'set_api_config'`).
   * @param {Object|null} [message=null] - Optional payload fields to include in the message.
   * @returns {boolean} `true` when the message was handed to the iFrame, `false` when it was dropped (the iFrame is not
   *   available, or `postMessage` threw). Callers that track what the iFrame has received should only count `true`.
   *
   * @example
   * // Configure the iFrame with API credentials
   * handler.sendMessage('set_api_config', { apiBaseUrl, apiToken });
   *
   * // Notify the iFrame without a payload
   * handler.sendMessage('open_create_direct_mail');
   *
   * ## Stale element self-healing
   * If `this.element` is missing or detached (e.g. the `<iframe>` was remounted via a
   * React `key` change and `reset()` hasn't caught up yet), this re-resolves the element
   * by id before giving up. If it's still unusable after that, the message is dropped
   * with a `console.warn` instead of throwing — a detached iframe has no `contentWindow`
   * to post into, and the DOM lookup fixes the common case where the new element already
   * exists but this instance's reference is just stale.
   *
   * ## Failure isolation
   * `postMessage` itself is wrapped in try/catch (e.g. a non-structured-clone-safe value
   * in `message` throws `DataCloneError`). A bad payload for one message type should not
   * break the caller's control flow or take down the rest of the integration — the error
   * is logged and swallowed here, same as the missing-element case above.
   */
  sendMessage(messageType, message = null) {
    // set_api_config carries the API credential: log it with the token redacted.
    console.log(
      `[IFrameMessageHandler] -> sent "${messageType}"`,
      messageType === "set_api_config" ? { ...message, apiToken: "[redacted]" } : message
    );

    if (!this.element?.contentWindow) {
      this.reset();
    }

    if (!this.element?.contentWindow) {
      console.warn(`IFrameMessageHandler: dropped "${messageType}" — iFrame "${this.iFrameHTMLId}" is not available`);
      return false;
    }

    try {
      this.element.contentWindow.postMessage({
        ...this.outgoingMessageConfig,
        type: messageType,
        ...message
      }, this.iFrameOriginUrl);

      return true;
    } catch (err) {
      console.error(`IFrameMessageHandler: failed to send "${messageType}"`, err);

      return false;
    }
  }
};

export default IFrameMessageHandler;